import { useMemo, useState } from 'react';
import { useCompany } from '../App';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import {
  Badge, Button, Card, DataTable, ErrorBanner, Field, Flash, Input, Modal, Select, Spinner, StatusBadge,
} from '../components/ui';
import { date, localToday, pricePerShare, securityType, shares } from '../lib/format';

const EQUITY_TYPES = ['COMMON_STOCK', 'PREFERRED_STOCK', 'OPTION', 'RSU', 'WARRANT'];
const CONVERTIBLE_TYPES = ['SAFE', 'CONVERTIBLE_NOTE'];

export default function Securities() {
  const { companyId, role } = useCompany();
  const [filter, setFilter] = useState('');
  const [action, setAction] = useState(null); // {kind, security}
  const [flash, setFlash] = useState(null);

  const list = useAsync(
    () => api.securities(companyId, filter ? { securityType: filter } : undefined),
    [companyId, filter]
  );
  const refs = useAsync(
    () =>
      Promise.all([
        api.stakeholders(companyId),
        api.shareClasses(companyId),
        api.equityPlans(companyId),
        api.vestingSchedules(companyId),
      ]).then(([stakeholders, shareClasses, plans, schedules]) => ({
        stakeholders, shareClasses, plans, schedules,
      })),
    [companyId]
  );

  const canEdit = ['LEGAL', 'ADMIN', 'OWNER'].includes(role);

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Securities</h1>
          <p>
            Each row is an instrument with its own certificate number and ledger. Issuance,
            transfer, cancellation and exercise all append to that ledger — quantities and
            dates are never edited in place. The certificate label and expiration date are
            pure administrative metadata, and can be corrected directly.
          </p>
        </div>
        <div className="page-actions">
          <Select value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: 180 }}>
            <option value="">All types</option>
            {[...EQUITY_TYPES, ...CONVERTIBLE_TYPES].map((t) => (
              <option key={t} value={t}>{securityType(t)}</option>
            ))}
          </Select>
          {canEdit ? (
            <Button variant="primary" onClick={() => setAction({ kind: 'issue' })}>
              Issue security
            </Button>
          ) : null}
        </div>
      </div>

      <Flash message={flash} onDone={() => setFlash(null)} />

      {list.loading ? (
        <Spinner />
      ) : list.error ? (
        <ErrorBanner error={list.error} onRetry={list.reload} />
      ) : (
        <Card padded={false}>
          <DataTable
            rowKey={(r) => r.id}
            empty="Nothing issued yet."
            columns={[
              { key: 'label', header: 'Certificate', render: (r) => <span className="primary-cell">{r.label}</span> },
              { key: 'holder', header: 'Holder', render: (r) => r.stakeholder_name },
              { key: 'type', header: 'Type', render: (r) => <Badge tone="neutral">{securityType(r.security_type)}</Badge> },
              { key: 'class', header: 'Class', render: (r) => r.share_class_name ?? '—' },
              { key: 'qty', header: 'Outstanding', align: 'right', numeric: true, render: (r) => shares(r.quantity_outstanding) },
              { key: 'issued', header: 'Issued', align: 'right', numeric: true, render: (r) => shares(r.issued_quantity) },
              { key: 'strike', header: 'Strike', align: 'right', numeric: true, render: (r) => (r.strike_price ? pricePerShare(r.strike_price) : '—') },
              { key: 'date', header: 'Issue date', render: (r) => date(r.issue_date) },
              { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
              {
                key: 'actions',
                header: '',
                render: (r) =>
                  canEdit ? (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <Button size="sm" variant="ghost" onClick={() => setAction({ kind: 'edit', security: r })}>
                        Edit
                      </Button>
                      {r.quantity_outstanding !== '0' ? (
                        <>
                          {['OPTION', 'WARRANT'].includes(r.security_type) ? (
                            <Button size="sm" variant="ghost" onClick={() => setAction({ kind: 'exercise', security: r })}>
                              Exercise
                            </Button>
                          ) : null}
                          <Button size="sm" variant="ghost" onClick={() => setAction({ kind: 'transfer', security: r })}>
                            Transfer
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setAction({ kind: 'cancel', security: r })}>
                            Cancel
                          </Button>
                        </>
                      ) : null}
                    </div>
                  ) : null,
              },
            ]}
            rows={list.data}
          />
        </Card>
      )}

      {action?.kind === 'issue' && refs.data ? (
        <IssueModal
          companyId={companyId}
          refs={refs.data}
          onClose={() => setAction(null)}
          onSaved={(label) => {
            setAction(null);
            setFlash(`Issued ${label}.`);
            list.reload();
          }}
        />
      ) : null}

      {action?.kind === 'edit' ? (
        <EditSecurityModal
          security={action.security}
          companyId={companyId}
          onClose={() => setAction(null)}
          onSaved={(msg) => {
            setAction(null);
            setFlash(msg);
            list.reload();
          }}
        />
      ) : null}

      {action && !['issue', 'edit'].includes(action.kind) && refs.data ? (
        <LedgerActionModal
          kind={action.kind}
          security={action.security}
          companyId={companyId}
          stakeholders={refs.data.stakeholders}
          onClose={() => setAction(null)}
          onSaved={(msg) => {
            setAction(null);
            setFlash(msg);
            list.reload();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Corrects a data-entry error on the certificate label or expiration date --
 * unlike a transfer/cancel/exercise, this never touches the ledger at all.
 */
function EditSecurityModal({ companyId, security, onClose, onSaved }) {
  const [form, setForm] = useState({
    label: security.label,
    // expiration_date arrives as a full timestamp (see date()'s own doc
    // comment in lib/format.js) -- an <input type="date"> needs exactly
    // YYYY-MM-DD or it silently renders blank.
    expirationDate: security.expiration_date ? String(security.expiration_date).slice(0, 10) : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const fe = error?.fieldErrors ?? {};

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.updateSecurity(companyId, security.id, {
        label: form.label,
        expiration_date: form.expirationDate || null,
      });
      onSaved(`Updated ${form.label}.`);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Edit security" onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={saving} onClick={submit}>Save</Button></>}>
      <ErrorBanner error={error} />
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label="Certificate label" required error={fe.label}>
            <Input value={form.label} onChange={set('label')} required autoFocus />
          </Field>
          <Field label="Expiration date" error={fe.expiration_date}>
            <Input type="date" value={form.expirationDate} onChange={set('expirationDate')} />
          </Field>
        </div>
      </form>
    </Modal>
  );
}

function IssueModal({ companyId, refs, onClose, onSaved }) {
  const [form, setForm] = useState({
    securityType: 'COMMON_STOCK',
    stakeholderId: '',
    shareClassId: '',
    equityPlanId: '',
    label: '',
    quantity: '',
    issueDate: localToday(),
    pricePerShare: '',
    strikePrice: '',
    vestingScheduleId: '',
    vestingStartDate: '',
    principal: '',
    valuationCap: '',
    discountRate: '',
    postMoney: 'true',
    interestRate: '',
    compounds: 'false',
    dayCountConvention: 'ACTUAL_365F',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const isConvertible = CONVERTIBLE_TYPES.includes(form.securityType);
  const isAward = ['OPTION', 'RSU', 'WARRANT'].includes(form.securityType);
  const needsStrike = ['OPTION', 'WARRANT'].includes(form.securityType);

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = {
        securityType: form.securityType,
        stakeholderId: form.stakeholderId,
        label: form.label,
        issueDate: form.issueDate,
      };
      if (!isConvertible) body.quantity = form.quantity;
      if (form.shareClassId) body.shareClassId = form.shareClassId;
      if (isAward && form.equityPlanId) body.equityPlanId = form.equityPlanId;
      if (form.pricePerShare) body.pricePerShare = form.pricePerShare;
      if (needsStrike && form.strikePrice) body.strikePrice = form.strikePrice;
      if (form.vestingScheduleId) {
        body.vestingScheduleId = form.vestingScheduleId;
        body.vestingStartDate = form.vestingStartDate || form.issueDate;
      }
      if (isConvertible) {
        body.convertible = {
          principal: form.principal,
          postMoney: form.postMoney === 'true',
          compounds: form.compounds === 'true',
          dayCountConvention: form.dayCountConvention,
        };
        if (form.valuationCap) body.convertible.valuationCap = form.valuationCap;
        if (form.discountRate) body.convertible.discountRate = form.discountRate;
        if (form.interestRate) body.convertible.interestRate = form.interestRate;
      }
      await api.issueSecurity(companyId, body);
      onSaved(form.label);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  const fe = error?.fieldErrors ?? {};

  return (
    <Modal
      title="Issue security"
      wide
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={submit}>Issue</Button>
        </>
      }
    >
      <ErrorBanner error={error} />
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label="Type" required>
            <Select value={form.securityType} onChange={set('securityType')}>
              <optgroup label="Equity">
                {EQUITY_TYPES.map((t) => <option key={t} value={t}>{securityType(t)}</option>)}
              </optgroup>
              <optgroup label="Convertible">
                {CONVERTIBLE_TYPES.map((t) => <option key={t} value={t}>{securityType(t)}</option>)}
              </optgroup>
            </Select>
          </Field>

          <Field label="Holder" required error={fe.stakeholderId}>
            <Select value={form.stakeholderId} onChange={set('stakeholderId')} required>
              <option value="">Select…</option>
              {refs.stakeholders.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </Field>

          <Field label="Certificate label" required hint="e.g. CS-1, PA-3, EO-14" error={fe.label}>
            <Input value={form.label} onChange={set('label')} required />
          </Field>

          <Field label="Issue date" required error={fe.issueDate}>
            <Input type="date" value={form.issueDate} onChange={set('issueDate')} required />
          </Field>

          {!isConvertible ? (
            <Field label="Quantity" required hint="Whole shares" error={fe.quantity}>
              <Input value={form.quantity} onChange={set('quantity')} inputMode="numeric" required />
            </Field>
          ) : null}

          {!isConvertible ? (
            <Field
              label="Share class"
              required={['COMMON_STOCK', 'PREFERRED_STOCK'].includes(form.securityType)}
              error={fe.shareClassId}
            >
              <Select value={form.shareClassId} onChange={set('shareClassId')}>
                <option value="">None</option>
                {refs.shareClasses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({shares(c.available)} available)
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}

          {isAward ? (
            <Field label="Equity plan" hint="Counts the grant against a pool">
              <Select value={form.equityPlanId} onChange={set('equityPlanId')}>
                <option value="">None</option>
                {refs.plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({shares(p.unallocated)} unallocated)
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}

          {needsStrike ? (
            <Field label="Strike price" required error={fe.strikePrice}>
              <Input value={form.strikePrice} onChange={set('strikePrice')} inputMode="decimal" required />
            </Field>
          ) : null}

          {!isConvertible ? (
            <Field label="Price per share" hint="What was paid, if anything">
              <Input value={form.pricePerShare} onChange={set('pricePerShare')} inputMode="decimal" />
            </Field>
          ) : null}

          {isAward ? (
            <>
              <Field label="Vesting schedule">
                <Select value={form.vestingScheduleId} onChange={set('vestingScheduleId')}>
                  <option value="">Fully vested on issue</option>
                  {refs.schedules.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} ({v.total_months}mo / {v.cliff_months}mo cliff)
                    </option>
                  ))}
                </Select>
              </Field>
              {form.vestingScheduleId ? (
                <Field label="Vesting start" hint="Defaults to the issue date">
                  <Input type="date" value={form.vestingStartDate} onChange={set('vestingStartDate')} />
                </Field>
              ) : null}
            </>
          ) : null}

          {isConvertible ? (
            <>
              <Field label="Principal" required hint="Cash invested" error={fe['convertible.principal']}>
                <Input value={form.principal} onChange={set('principal')} inputMode="decimal" required />
              </Field>
              <Field label="Valuation cap">
                <Input value={form.valuationCap} onChange={set('valuationCap')} inputMode="decimal" />
              </Field>
              <Field label="Discount" hint="As a fraction, e.g. 0.2 for 20%">
                <Input value={form.discountRate} onChange={set('discountRate')} inputMode="decimal" />
              </Field>
              <Field label="Cap basis" hint="Post-money is the current YC standard">
                <Select value={form.postMoney} onChange={set('postMoney')}>
                  <option value="true">Post-money</option>
                  <option value="false">Pre-money</option>
                </Select>
              </Field>
              <Field label="Interest rate" hint="As a fraction, e.g. 0.08 for 8%. Leave blank for no interest.">
                <Input value={form.interestRate} onChange={set('interestRate')} inputMode="decimal" />
              </Field>
              <Field label="Compounds" hint="Simple interest unless you choose Compounding">
                <Select value={form.compounds} onChange={set('compounds')}>
                  <option value="false">Simple</option>
                  <option value="true">Compounding</option>
                </Select>
              </Field>
              <Field label="Day-count convention" hint="How the interest year-fraction is measured between dates">
                <Select value={form.dayCountConvention} onChange={set('dayCountConvention')}>
                  <option value="ACTUAL_365F">Actual/365 fixed</option>
                  <option value="30_360">30/360</option>
                </Select>
              </Field>
              <div className="field is-full">
                <span className="field-hint">
                  A convertible has no share count until a priced round converts it, so it is
                  excluded from fully diluted until then and reported separately.
                </span>
              </div>
            </>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}

function LedgerActionModal({ kind, security, companyId, stakeholders, onClose, onSaved }) {
  const today = localToday();
  const [form, setForm] = useState({
    quantity: '',
    toStakeholderId: '',
    newLabel: '',
    effectiveDate: today,
    exerciseDate: today,
    reason: 'FORFEITED',
    paymentMethod: 'CASH',
    earlyExercise: 'false',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const titles = { transfer: 'Transfer shares', cancel: 'Cancel shares', exercise: 'Exercise options' };

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (kind === 'transfer') {
        await api.transferSecurity(companyId, security.id, {
          toStakeholderId: form.toStakeholderId,
          quantity: form.quantity,
          newLabel: form.newLabel,
          effectiveDate: form.effectiveDate,
        });
        onSaved(`Transferred ${form.quantity} from ${security.label}.`);
      } else if (kind === 'cancel') {
        await api.cancelSecurity(companyId, security.id, {
          quantity: form.quantity || undefined,
          effectiveDate: form.effectiveDate,
          reason: form.reason,
        });
        onSaved(`Cancelled shares on ${security.label}.`);
      } else {
        await api.exerciseSecurity(companyId, security.id, {
          quantity: form.quantity,
          exerciseDate: form.exerciseDate,
          newLabel: form.newLabel,
          paymentMethod: form.paymentMethod,
          earlyExercise: form.earlyExercise === 'true',
        });
        onSaved(`Exercised ${form.quantity} of ${security.label}.`);
      }
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={titles[kind]}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant={kind === 'cancel' ? 'danger' : 'primary'} loading={saving} onClick={submit}>
            {titles[kind]}
          </Button>
        </>
      }
    >
      <ErrorBanner error={error} />
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        {security.label} · {security.stakeholder_name} · {shares(security.quantity_outstanding)} outstanding
      </p>
      <form onSubmit={submit}>
        <Field
          label="Quantity"
          required={kind !== 'cancel'}
          hint={kind === 'cancel' ? 'Leave blank to cancel the whole position' : undefined}
        >
          <Input value={form.quantity} onChange={set('quantity')} inputMode="numeric" />
        </Field>

        {kind === 'transfer' ? (
          <>
            <Field label="Transfer to" required>
              <Select value={form.toStakeholderId} onChange={set('toStakeholderId')} required>
                <option value="">Select…</option>
                {stakeholders
                  .filter((s) => s.id !== security.stakeholder_id)
                  .map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Select>
            </Field>
            <Field label="New certificate label" required hint="The recipient's new certificate">
              <Input value={form.newLabel} onChange={set('newLabel')} required />
            </Field>
            <Field label="Effective date" required>
              <Input type="date" value={form.effectiveDate} onChange={set('effectiveDate')} required />
            </Field>
          </>
        ) : null}

        {kind === 'cancel' ? (
          <>
            <Field label="Reason">
              <Select value={form.reason} onChange={set('reason')}>
                {['FORFEITED', 'EXPIRED', 'REPURCHASE', 'CLAWBACK', 'OTHER'].map((r) => (
                  <option key={r} value={r}>{r.toLowerCase()}</option>
                ))}
              </Select>
            </Field>
            <Field label="Effective date" required>
              <Input type="date" value={form.effectiveDate} onChange={set('effectiveDate')} required />
            </Field>
          </>
        ) : null}

        {kind === 'exercise' ? (
          <>
            <Field label="New certificate label" required hint="The stock this becomes">
              <Input value={form.newLabel} onChange={set('newLabel')} required />
            </Field>
            <Field label="Exercise date" required>
              <Input type="date" value={form.exerciseDate} onChange={set('exerciseDate')} required />
            </Field>
            <Field label="Payment method">
              <Select value={form.paymentMethod} onChange={set('paymentMethod')}>
                {['CASH', 'CHECK', 'WIRE', 'CASHLESS', 'NET_EXERCISE', 'PROMISSORY_NOTE'].map((m) => (
                  <option key={m} value={m}>{m.replace(/_/g, ' ').toLowerCase()}</option>
                ))}
              </Select>
            </Field>
            <Field
              label="Early exercise"
              hint="Exercising unvested shares changes the 83(b) and tax position — only with advice."
            >
              <Select value={form.earlyExercise} onChange={set('earlyExercise')}>
                <option value="false">Vested shares only</option>
                <option value="true">Allow unvested (early exercise)</option>
              </Select>
            </Field>
          </>
        ) : null}
      </form>
    </Modal>
  );
}
